export type HostAgreementStatus = "agreed" | "mismatch" | "unavailable";

export type HostOrderSnapshot = {
  shop: string;
  serial: string;
  model: string;
  size: string;
  event: string;
  orderNumber: string;
};

export type HostAgreement = {
  status: HostAgreementStatus;
  notes: string[];
};

function compact(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function conflicts(local: string, host: string): boolean {
  const left = compact(local);
  const right = compact(host);
  if (!left || !right) return false;
  if (left === right) return false;
  return !left.includes(right) && !right.includes(left);
}

/**
 * Final QC is ready-to-ship. ShedSuite agreement confirms the same building
 * when a snapshot is on the unit. A missing snapshot does not block the pass.
 */
export function compareReadyToShipWithHost(args: {
  sessionSerial: string;
  sessionModel: string;
  host: HostOrderSnapshot | null;
}): HostAgreement {
  const host = args.host;
  const hasSnapshot = Boolean(
    host && (host.orderNumber || host.serial || host.model || host.shop || host.size)
  );
  if (!host || !hasSnapshot) {
    return { status: "unavailable", notes: ["ShedSuite has no order snapshot on this unit."] };
  }

  const notes: string[] = [];
  if (host.event.toLowerCase().includes("cancel")) notes.push("ShedSuite cancelled this order.");
  if (conflicts(args.sessionSerial, host.serial)) notes.push("Serial does not match ShedSuite.");
  if (conflicts(args.sessionModel, host.model)) notes.push("Model does not match ShedSuite.");
  if (notes.length > 0) return { status: "mismatch", notes };

  const label = host.orderNumber ? `ShedSuite order ${host.orderNumber}` : "ShedSuite snapshot";
  return { status: "agreed", notes: [`${label} matches this building.`] };
}

export function readHostAgreement(summary: Record<string, unknown> | null | undefined): HostAgreement | null {
  const raw = summary?.host_agreement;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const status = row.status;
  if (status !== "agreed" && status !== "mismatch" && status !== "unavailable") return null;
  const notes = Array.isArray(row.notes) ? row.notes.filter((note): note is string => typeof note === "string") : [];
  return { status, notes };
}
